
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import apiClient from "@common/api-client";
import { type NavItem } from "@common/api-client/presentation";
import { BoundingArea } from "@common/components/_models";
import usePendingChanges from "@common/composables/usePendingChanges";
import { useStoreInitializer } from "@common/stores/_helpers";

export const useAppStore = defineStore("app", () => {
  const pendingChanges = usePendingChanges();
  const loading = computed(() => pendingChanges.loading.value);

  // ----------------------------
  // LAYOUT
  // ----------------------------
  const lastResize = ref(0);

  const headerId = "appHeader";
  const mainId = "appMain";
  const footerId = "appFooter";
  const mainNavId = "appMainNav";

  // Cache elements (non-reactive)
  let headerEl: HTMLElement | null = null;
  let mainEl: HTMLElement | null = null;
  let footerEl: HTMLElement | null = null;
  let mainNavEl: HTMLElement | null = null;

  // ResizeObserver should NOT be reactive
  let resizeObserver: ResizeObserver | null = null;

  const newBoundingArea = (): BoundingArea => ({
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: 0,
    height: 0,
  });

  const documentBoundingArea = ref<BoundingArea>(newBoundingArea());
  const contentBoundingArea = ref<BoundingArea>(newBoundingArea());

  function registerElementsOnce() {
    // Use getElementById (faster than querySelector) and only fill if missing.
    headerEl ??= document.getElementById(headerId) as HTMLElement | null;
    mainEl ??= document.getElementById(mainId) as HTMLElement | null;
    footerEl ??= document.getElementById(footerId) as HTMLElement | null;
    mainNavEl ??= document.getElementById(mainNavId) as HTMLElement | null;
  }

  // Navigation
  const navigating = ref(false);

  // MAIN Header
  const showHeader = ref(true);
  function toggleHeader(val?: boolean) {
    showHeader.value = val === undefined ? !showHeader.value : val;
  }

  // MAIN NAV
  const showMainNav = ref(false);
  function toggleNav(val?: boolean) {
    showMainNav.value = val === undefined ? !showMainNav.value : val;
  }

  // FOOTER
  const showFooter = ref(true);
  function toggleFooter(val?: boolean) {
    showFooter.value = val === undefined ? !showFooter.value : val;
  }

  // Throttle layout calculations to once per frame
  let resizeScheduled = false;

  function computeAndCommitLayout() {
    // NOTE: reads like offsetHeight/offsetWidth can force layout,
    // so we batch them into a single rAF callback.
    const top = showHeader.value ? (headerEl?.offsetHeight ?? 0) : 0;
    const bottom = showFooter.value ? (footerEl?.offsetHeight ?? 0) : 0;
    const left = showMainNav.value ? (mainNavEl?.offsetWidth ?? 0) : 0;

    // Prefer window inner dimensions (less layout dependent)
    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;

    // main content width: if mainEl exists, use clientWidth; else fallback
    const mainWidth = mainEl?.clientWidth ?? (viewportW - left);

    const docArea: BoundingArea = {
      top,
      bottom,
      left,
      right: viewportW,
      width: mainWidth,
      height: viewportH - top - bottom,
    };

    // Commit atomically (1 reactive write each)
    documentBoundingArea.value = docArea;
    contentBoundingArea.value = { ...docArea };

    lastResize.value = Date.now();
  }

  function scheduleResize() {
    if (resizeScheduled) return;
    resizeScheduled = true;

    requestAnimationFrame(() => {
      resizeScheduled = false;
      // Ensure our cached elements exist (in case DOM mounted late)
      registerElementsOnce();
      computeAndCommitLayout();
    });
  }

  // ResizeObserver callback should be light and just schedule
  function onObservedResize() {
    scheduleResize();
  }

  const registerObservers = (): Promise<void> => {
    return new Promise(resolve => {
      const tryRegister = () => {
        registerElementsOnce();

        if (!mainEl) {
          // screen not ready, so wait and retry
          setTimeout(tryRegister, 100);
          return;
        }

        // Disconnect an existing observer just in case init runs twice
        if (resizeObserver) resizeObserver.disconnect();

        resizeObserver = new ResizeObserver(onObservedResize);

        // Observe only what matters for layout offsets
        if (headerEl) resizeObserver.observe(headerEl);
        if (mainNavEl) resizeObserver.observe(mainNavEl);
        if (footerEl) resizeObserver.observe(footerEl);

        // initial measurement
        scheduleResize();

        resolve();
      };

      tryRegister();
    });
  };

  // Keep watches, but call the throttled scheduler (cheap)
  watch([showHeader, showMainNav, showFooter], () => {
    scheduleResize();
  });

  // ----------------------------
  // MAIN NAV ITEMS
  // ----------------------------
  const mainNavItems = ref<NavItem[]>([]);

  async function loadNavItems() {
    try {
      pendingChanges.increment();
      mainNavItems.value = await apiClient.presentation.getNavItems();
    } catch (error) {
      console.error("Failed to load navigation items:", error);
    } finally {
      pendingChanges.decrement();
    }
  }

  // ----------------------------
  // CLEANUP
  // ----------------------------
  function dispose() {
    resizeObserver?.disconnect();
    resizeObserver = null;
  }

  // ----------------------------
  // INITIALIZE
  // ----------------------------
  const { init, initialized } = useStoreInitializer(
    async () => {
      await Promise.all([registerObservers(), loadNavItems()]);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (error: any) => {
      throw error;
    }
  );

  init();

  return {
    initialized,
    loading,

    // LAYOUT
    lastResize,
    documentBoundingArea,
    contentBoundingArea,

    navigating,

    // HEADER
    showHeader,
    toggleHeader,

    // MAIN NAV
    mainNavItems,
    showMainNav,
    toggleNav,

    // FOOTER
    showFooter,
    toggleFooter,

    // CLEANUP
    dispose,
  };
});
